import * as ents from '@attain-apps/entities'

import type * as me from '../../me'

/**
 * Normalized route access target used by the Admin Center guard.
 */
export interface RouteAccess {
  readonly scope: RouteAccess.Scope | undefined
  readonly tenantId: string | undefined
  readonly zoneId: string | undefined
  readonly appId: string | undefined
  readonly scenarioId: string | undefined
}

export namespace RouteAccess {
  /**
   * Route-level Admin Center access scopes.
   */
  export enum Scope {
    AdminCenter = 'adminCenter',
    App = 'app',
    PlatformKnowledgeBases = 'platformKnowledgeBases',
    PlatformUsers = 'platformUsers',
    Scenario = 'scenario',
    Tenant = 'tenant',
    Zone = 'zone',
  }

  /**
   * Determines whether a user can enter any Admin Center route.
   */
  export function canAccessAdminCenter(
    user: me.models.User | undefined,
  ): boolean {
    return (
      Boolean(user?.isPlatformAdmin) ||
      Boolean(user?.tenants.some(hasTenantAdminPortalAccess))
    )
  }

  /**
   * Checks whether the loaded user access tree and zone apps allow the route.
   */
  export function canAccess(
    user: me.models.User | undefined,
    route: RouteAccess,
    apps?: readonly ents.core.admin.App.ForUser[],
  ): boolean {
    if (!user) {
      return false
    }
    if (user.isPlatformAdmin) {
      return true
    }

    switch (route.scope) {
      case Scope.AdminCenter:
        return canAccessAdminCenter(user)
      case Scope.PlatformKnowledgeBases:
      case Scope.PlatformUsers:
        return false
      case Scope.Tenant:
        return getTenantForRoute(user, route) !== undefined
      case Scope.Zone:
        return (
          canManageUsersForTenantChildRoute(user, route, Scope.Zone) ||
          getZoneForRoute(user, route) !== undefined
        )
      case Scope.App:
        return (
          canManageUsersForTenantChildRoute(user, route, Scope.App) ||
          getAppForRoute(route, apps) !== undefined
        )
      case Scope.Scenario:
        return (
          canManageUsersForTenantChildRoute(user, route, Scope.Scenario) ||
          getScenarioForRoute(route, apps) !== undefined
        )
      case undefined:
        return false
      default:
        return false
    }
  }

  /** Whether this route needs its zone's app list for membership checks. */
  export function needsAppLookup(
    user: me.models.User | undefined,
    route: RouteAccess,
  ): boolean {
    if (!user || user.isPlatformAdmin) {
      return false
    }
    const scope = route.scope
    return (
      (scope === Scope.App || scope === Scope.Scenario) &&
      hasRequiredChildRouteIds(route, scope) &&
      !canManageUsersForTenantChildRoute(user, route, scope) &&
      getZoneForRoute(user, route) !== undefined
    )
  }

  /**
   * Determines whether a tenant assignment grants Tenant Admin Portal entry.
   */
  function hasTenantAdminPortalAccess(tenant: me.models.Tenant): boolean {
    return ents.core.admin.Access.grantsTenantAdminPortalAccess(tenant.access)
  }

  /**
   * Allows assignment managers to enter tenant children even when they do not
   * have direct Main App access to that child entity.
   */
  function canManageUsersForTenantChildRoute(
    user: me.models.User,
    route: RouteAccess,
    scope: Scope.Zone | Scope.App | Scope.Scenario,
  ): boolean {
    if (!hasRequiredChildRouteIds(route, scope)) {
      return false
    }

    return ents.core.admin.Access.allows(
      getTenantForRoute(user, route)?.access,
      ents.core.admin.Access.Flag.CanManageUsers,
    )
  }

  /**
   * Checks that the route has the ID path required by the requested child scope.
   */
  function hasRequiredChildRouteIds(
    route: RouteAccess,
    scope: Scope.Zone | Scope.App | Scope.Scenario,
  ): boolean {
    if (!route.zoneId) {
      return false
    }
    if (scope === Scope.Zone) {
      return true
    }
    if (!route.appId) {
      return false
    }
    if (scope === Scope.App) {
      return true
    }
    return Boolean(route.scenarioId)
  }

  /**
   * Returns the tenant if the route targets a tenant the current user administers.
   */
  function getTenantForRoute(
    user: me.models.User,
    route: RouteAccess,
  ): me.models.Tenant | undefined {
    return route.tenantId
      ? user.tenants.find(
          tenant =>
            tenant.id === route.tenantId && hasTenantAdminPortalAccess(tenant),
        )
      : undefined
  }

  /**
   * Returns the zone if it exists in the user's administered tenant tree.
   */
  function getZoneForRoute(
    user: me.models.User,
    route: RouteAccess,
  ): me.models.Zone | undefined {
    return route.zoneId
      ? getTenantForRoute(user, route)?.zones.find(
          zone => zone.id === route.zoneId,
        )
      : undefined
  }

  /**
   * Returns the targeted app from the loaded zone apps.
   */
  function getAppForRoute(
    route: RouteAccess,
    apps?: readonly ents.core.admin.App.ForUser[],
  ): ents.core.admin.App.ForUser | undefined {
    return route.appId ? apps?.find(app => app.id === route.appId) : undefined
  }

  /**
   * Returns the targeted scenario from the loaded zone apps.
   */
  function getScenarioForRoute(
    route: RouteAccess,
    apps?: readonly ents.core.admin.App.ForUser[],
  ): ents.core.admin.Scenario.ForUser | undefined {
    return route.scenarioId
      ? getAppForRoute(route, apps)?.scenarios.find(
          scenario => scenario.id === route.scenarioId,
        )
      : undefined
  }
}

/**
 * Admin Center action permission input for tenant-owned content.
 */
export interface ContentActionAccess {
  readonly context: ents.core.admin.Context | undefined
  readonly entity?: ents.core.common.CreatedBy<ents.core.admin.User.Id>
  readonly user: me.models.User | undefined
}

export namespace ContentActionAccess {
  /**
   * Determines whether the current admin user can create platform tenants.
   */
  export function canCreateTenant(user: me.models.User | undefined): boolean {
    return Boolean(user?.isPlatformAdmin)
  }

  /**
   * Determines whether the current admin user can edit tenant information.
   */
  export function canEditTenant({
    context,
    user,
  }: Pick<ContentActionAccess, 'context' | 'user'>): boolean {
    return (
      (context !== undefined && Boolean(user?.isPlatformAdmin)) ||
      (context !== undefined &&
        ents.core.admin.Access.allows(
          _findTenant(user, context)?.access,
          ents.core.admin.Access.Flag.CanConfigureInfo,
        ))
    )
  }

  /**
   * Determines whether the current admin user can delete platform tenants.
   */
  export function canDeleteTenant(user: me.models.User | undefined): boolean {
    return Boolean(user?.isPlatformAdmin)
  }

  /**
   * Determines whether the current admin user can create tenant child content.
   */
  export function canCreateChild({
    context,
    user,
  }: Pick<ContentActionAccess, 'context' | 'user'>): boolean {
    return (
      (context !== undefined && Boolean(user?.isPlatformAdmin)) ||
      (context !== undefined &&
        ents.core.admin.Access.allows(
          _findTenant(user, context)?.access,
          ents.core.admin.Access.Flag.CanCreateChildren,
        ))
    )
  }

  /**
   * Determines whether the current admin user can edit tenant child content.
   */
  export function canEditChild({
    context,
    entity,
    user,
  }: ContentActionAccess): boolean {
    if (!context || !entity) {
      return false
    }
    if (user?.isPlatformAdmin) {
      return true
    }

    const tenantAccess = _findTenant(user, context)?.access
    if (
      ents.core.admin.Access.allows(
        tenantAccess,
        ents.core.admin.Access.Flag.CanConfigureInfo,
      )
    ) {
      return true
    }

    return (
      entity?.createdBy === user?.id &&
      ents.core.admin.Access.allows(
        tenantAccess,
        ents.core.admin.Access.Flag.CanCreateChildren,
      )
    )
  }

  /**
   * Determines whether the current admin user can edit owned tenant child content.
   */
  export function canEditOwnedChild({
    context,
    entity,
    user,
  }: ContentActionAccess): boolean {
    if (!context || !entity) {
      return false
    }

    return (
      Boolean(user?.isPlatformAdmin) ||
      (entity?.createdBy === user?.id &&
        ents.core.admin.Access.allows(
          _findTenant(user, context)?.access,
          ents.core.admin.Access.Flag.CanCreateChildren,
        ))
    )
  }

  /**
   * Determines whether the current admin user can delete tenant child content.
   */
  export function canDeleteChild({
    context,
    entity,
    user,
  }: ContentActionAccess): boolean {
    if (!context || !entity) {
      return false
    }

    if (user?.isPlatformAdmin) {
      return true
    }

    const tenantAccess = _findTenant(user, context)?.access
    if (entity?.createdBy === user?.id) {
      return ents.core.admin.Access.allows(
        tenantAccess,
        ents.core.admin.Access.Flag.CanCreateChildren,
      )
    }

    return ents.core.admin.Access.allows(
      tenantAccess,
      ents.core.admin.Access.Flag.CanDeleteUnownedChildren,
    )
  }

  /**
   * Finds the tenant assignment used for Admin Center content permissions.
   */
  function _findTenant(
    user: me.models.User | undefined,
    context: ents.core.admin.Context | undefined,
  ): me.models.Tenant | undefined {
    return context?.tenantId
      ? user?.tenants.find(tenant => tenant.id === context.tenantId)
      : undefined
  }
}
